import { Bike, Shirt, Thermometer, Umbrella, type LucideIcon } from 'lucide-react';
import {
  BIKE_LEVEL_LABELS as BIKE,
  JACKET_LEVEL_LABELS as JACKET,
  LAUNDRY_LEVEL_LABELS as LAUNDRY,
  UMBRELLA_LEVEL_LABELS as UMBRELLA,
  type DailyIndex,
} from '../../../shared/domain/index.ts';
import { formatTime } from '../../lib/format.ts';

const dayWord = (day: 'today' | 'tomorrow') => (day === 'today' ? 'aujourd’hui' : 'demain');

/** Plage couverte par parapluie, tenue et vélo (voir `outingWindow` côté domaine). */
const outing = (day: 'today' | 'tomorrow') => (day === 'today' ? 'd’ici 22:00' : 'demain en journée');

export interface DescribedIndex {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}

/** Libellé d'un niveau ; `undefined` pour un niveau inconnu de ce code (API plus récente). */
const labelOf = (labels: Readonly<Record<string, string>>, level: string): string | undefined => labels[level];

function describeKnown(index: DailyIndex): (Omit<DescribedIndex, 'value'> & { value: string | undefined }) | null {
  switch (index.kind) {
    case 'laundry':
      return {
        icon: Shirt,
        label: 'Linge dehors',
        value: labelOf(LAUNDRY, index.level),
        detail:
          index.rainFrom === undefined
            ? `${dayWord(index.day)}, de 10:00 à 17:00`
            : `${dayWord(index.day)} · pluie vers ${formatTime(index.rainFrom)}`,
      };
    case 'umbrella':
      return {
        icon: Umbrella,
        label: 'Parapluie',
        value: labelOf(UMBRELLA, index.level),
        detail:
          index.rainFrom === undefined || index.rainUntil === undefined
            ? `pas de pluie prévue ${outing(index.day)}`
            : `${dayWord(index.day)} · pluie de ${formatTime(index.rainFrom)} à ${formatTime(index.rainUntil)}`,
      };
    case 'jacket':
      return {
        icon: Thermometer,
        label: 'Tenue',
        value: labelOf(JACKET, index.level),
        detail: `ressenti de ${index.feelsLikeMinC} à ${index.feelsLikeMaxC} °C ${outing(index.day)}`,
      };
    case 'bike':
      return {
        icon: Bike,
        label: 'Vélo',
        value: labelOf(BIKE, index.level),
        detail:
          index.rainFrom === undefined
            ? `rafales jusqu’à ${index.maxGustKmh} km/h ${outing(index.day)}`
            : `${dayWord(index.day)} · pluie vers ${formatTime(index.rainFrom)}`,
      };
    default:
      return null;
  }
}

/**
 * Libellé, valeur et précision d'un indice. Un indice d'un type ou d'un niveau inconnu (API plus récente que ce
 * code) renvoie `null` et n'est pas affiché : jamais sous l'étiquette d'un autre indice.
 */
export function describeIndex(index: DailyIndex): DescribedIndex | null {
  const described = describeKnown(index);
  return described === null || described.value === undefined ? null : { ...described, value: described.value };
}
